import { OutputMigration } from '@amzn/awscat-portfolio-assessment-types';
import { Alert, Header, SpaceBetween, Table, TableProps } from '@amzn/awsui-components-react';
import { useEffect, useState } from 'react';

import { constants } from '../../common/Localization';
import { formatCurrencyNoDecimals } from '../../common/LocalizationHelper';
import { getCell } from '../common/awsui-table-helper';

interface TableItem {
    label: string;
    yearlyCost: number[];
    total: number;
}

interface Props {
    migration: OutputMigration;
}

function MigrationCashflowCostDetails({ migration }: Props) {
    const roi = migration.roi;
    const currency = migration.currency;

    const [ columnDefinitions, setColumnDefinitions ] = useState<TableProps.ColumnDefinition<TableItem>[]>([]);
    const [ items, setItems ] = useState<TableItem[]>([]);

    useEffect(() => {
        const createColumnDefinitions = (): void => {
            const colDefs: TableProps.ColumnDefinition<TableItem>[] = [
                {
                    id: 'label',
                    header: '',
                    cell: item => getCell(item.label, item.label.includes(constants.TOTAL), 'left')
                }
            ];

            roi.awsMigrationCashFlow.yearly.forEach((year, index) => {
                colDefs.push({
                    id: `year${index + 1}`,
                    header: getCell(`${constants.YEAR} ${index + 1}`),
                    cell: item => getCell(formatCurrencyNoDecimals(item.yearlyCost[index] ?? 0, currency),
                        item.label.includes(constants.TOTAL))
                });
            });

            colDefs.push({
                id: 'total',
                header: getCell(constants.TOTAL, true),
                cell: item => getCell(formatCurrencyNoDecimals(item.total, currency),
                    item.label.includes(constants.TOTAL))
            });

            setColumnDefinitions(colDefs);
        };

        const createItems = (): void => {
            const items = [
                {
                    label: constants.AWS_COST_WITH_MIGRATION,
                    yearlyCost: roi.awsMigrationCashFlow.yearly,
                    total: roi.awsMigrationCashFlow.total ?? 0
                },
                {
                    label: constants.ON_PREM_COST_WITH_MIGRATION,
                    yearlyCost: roi.onPremisesCashFlow.withMigration.yearly,
                    total: roi.onPremisesCashFlow.withMigration.total ?? 0
                },
                {
                    label: constants.MIGRATION_COST,
                    yearlyCost: migration.cashFlow.yearly,
                    total: migration.totalMigrationCost ?? 0
                },
                {
                    label: constants.TOTAL_COST_WITH_MIGRATION,
                    yearlyCost: roi.totalCostWithMigrationCashFlow.yearly,
                    total: roi.totalCostWithMigration ?? 0
                },
                {
                    label: constants.TOTAL_ON_PREM_WITHOUT_MIGRATION,
                    yearlyCost: roi.onPremisesCashFlow.withoutMigration.yearly,
                    total: roi.onPremisesCashFlow.withoutMigration.total ?? 0
                }
            ];
            setItems(items);
        };

        createColumnDefinitions();
        createItems();
    }, [migration, roi, currency]);

    return (
        <SpaceBetween size='xxs'>
            <Header variant='h2'>{constants.CASH_FLOW_DETAILS}</Header>
            { migration.roi.awsMigrationCashFlow ? (
                <Table
                    items={items}
                    columnDefinitions={columnDefinitions}
                />
            ) : (
                <Alert>
                    {constants.RECALCULATE_MESSAGE}
                </Alert>
            )}
        </SpaceBetween>
    );
}

export default MigrationCashflowCostDetails;
